import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../lib/api';
import { copyText, timeAgo } from '../lib/format';
import { CATEGORY_LABELS, type Asset, type Comment, type CommentStatus, type Item, type Person, type Reply } from '../lib/types';
import { locationOf } from './location';
import { Icon } from '../components/Icon';
import { Avatar, ConfirmDialog, Menu, type MenuEntry } from '../components/ui';
import { useToast } from '../components/toast';
import { CommentInput } from './CommentInput';
import type { CommentActions } from './useComments';

const STATUS_LABEL: Record<CommentStatus, string> = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' };

function Attachments({ items }: { items: Asset[] }) {
  if (!items.length) return null;
  return (
    <div className="thread-attachments">
      {items.map((asset) =>
        asset.media === 'image' ? (
          <a key={asset.id} href={asset.url} target="_blank" rel="noreferrer" className="thread-image" title={asset.filename}>
            <img src={asset.url} alt={asset.filename} loading="lazy" />
          </a>
        ) : (
          <a key={asset.id} href={asset.downloadUrl} className="attach-chip" title={`Download ${asset.filename}`}>
            <Icon name="file" />
            <span className="truncate">{asset.filename}</span>
          </a>
        ),
      )}
    </div>
  );
}

function authorKind(author: Comment['author'], people: Person[]): 'team' | 'client' | 'api' {
  if (author.kind === 'api') return 'api';
  const person = people.find((p) => p.id === author.id);
  return person && person.role !== 'client' ? 'team' : person ? 'client' : 'team';
}

/** Highlights @mentions without parsing anything as HTML. */
function Body({ text }: { text: string }) {
  const parts = text.split(/(@[\w.-]+(?:\s[A-Z][\w.-]*)?)/g);
  return (
    <p className="thread-body">
      {parts.map((part, index) =>
        part.startsWith('@') ? (
          <span key={index} className="mention">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </p>
  );
}

interface Props {
  comment: Comment;
  item?: Item | null;
  projectId: string;
  people: Person[];
  selected: boolean;
  highlighted?: boolean;
  canComment: boolean;
  canManage: boolean;
  currentUserId: string | null;
  actions: CommentActions;
  linkFor: (comment: Comment) => string;
  onSelect: () => void;
  onHover?: (hovering: boolean) => void;
  dimmed?: boolean;
}

export function Thread({ comment, item, projectId, people, selected, highlighted, canComment, canManage, currentUserId, actions, linkFor, onSelect, onHover, dimmed }: Props) {
  const toast = useToast();
  const ref = useRef<HTMLElement>(null);
  const [editing, setEditing] = useState<string | null>(null); // comment id or reply id being edited
  const [confirm, setConfirm] = useState<{ kind: 'comment' } | { kind: 'reply'; reply: Reply } | null>(null);
  const location = locationOf(comment, item);
  const mine = (authorId: string | null) => !!currentUserId && authorId === currentUserId;

  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selected]);

  const running = useRef(false);
  const run = async (task: () => Promise<void>) => {
    if (running.current) return; // a second click while the first is still on its way
    running.current = true;
    try {
      await task();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      running.current = false;
    }
  };

  const menu: MenuEntry[] = [
    {
      label: 'Copy link',
      icon: 'link',
      onSelect: async () => {
        if (await copyText(linkFor(comment))) toast.show('Link copied');
        else toast.error('Could not copy the link. Copy it from the address bar instead.');
      },
    },
  ];
  if (canComment) {
    (['open', 'in_progress', 'resolved'] as CommentStatus[])
      .filter((status) => status !== comment.status)
      .forEach((status) => menu.push({ label: `Mark ${STATUS_LABEL[status].toLowerCase()}`, icon: status === 'resolved' ? 'check' : 'activity', onSelect: () => run(() => actions.setStatus(comment.id, status)) }));
    menu.push({ label: 'Mark as unread', icon: 'eye', onSelect: () => run(() => actions.markUnread(comment.id)) });
    if (mine(comment.author.id) || canManage) {
      if (mine(comment.author.id) || canManage) menu.push({ label: 'Edit', icon: 'pencil', onSelect: () => setEditing(comment.id) });
      menu.push({ label: 'Delete', icon: 'trash', danger: true, onSelect: () => setConfirm({ kind: 'comment' }) });
    }
  }

  return (
    <article
      ref={ref}
      className={`thread ${selected ? 'is-selected' : ''} ${highlighted ? 'is-highlighted' : ''} ${dimmed ? 'is-dimmed' : ''} status-${comment.status}`}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      data-comment-id={comment.id}
    >
      <div className="thread-head">
        <div
          className="thread-head-main"
          role="button"
          tabIndex={0}
          aria-expanded={selected}
          onClick={onSelect}
          onKeyDown={(event) => {
            if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              onSelect();
            }
          }}
        >
          <span className={`pin-badge s-${comment.status}`} aria-label={`Revision ${comment.number}, ${STATUS_LABEL[comment.status]}`}>
            {comment.number}
          </span>
          <div className="grow">
            <div className="thread-meta">
              <span className="thread-author truncate">{comment.author.name}</span>
              <span className="muted">{timeAgo(comment.createdAt)}</span>
              {comment.unread && <span className="dot" title="New activity" />}
            </div>
            <div className="thread-tags">
              {location && (
                <span className="tag" title={comment.anchor.type === 'dom' ? comment.anchor.pageUrl || undefined : undefined}>
                  <Icon name={location.icon} size={12} />
                  <span className="truncate">{location.label}</span>
                </span>
              )}
              {comment.category && <span className="tag">{CATEGORY_LABELS[comment.category] || comment.category}</span>}
              {comment.status === 'in_progress' && <span className="tag tag-progress">In progress</span>}
              {comment.status === 'resolved' && <span className="tag tag-resolved">Resolved</span>}
              {comment.author.kind === 'api' && <span className="tag">via API</span>}
            </div>
          </div>
        </div>
        <Menu trigger={<Icon name="more" />} label={`Actions for revision ${comment.number}`} items={menu} />
      </div>

      {editing === comment.id ? (
        <div className="thread-section">
          <CommentInput
            projectId={projectId}
            people={people}
            placeholder="Edit revision"
            submitLabel="Save"
            initialBody={comment.body}
            allowAttachments={false}
            autoFocus
            onCancel={() => setEditing(null)}
            onSubmit={async (draft) => {
              await actions.edit(comment.id, draft.body);
              setEditing(null);
            }}
          />
        </div>
      ) : (
        <div className={`thread-section ${selected ? '' : 'is-clamped'}`} onClick={selected ? undefined : onSelect}>
          <Body text={comment.body} />
          {comment.editedAt && selected && <span className="muted thread-edited">edited</span>}
          <Attachments items={comment.attachments} />
        </div>
      )}

      {!selected && comment.replies.length > 0 && (
        <button type="button" className="thread-replies-count" onClick={onSelect}>
          <Icon name="message" size={12} /> {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
        </button>
      )}

      {selected && (
        <>
          {comment.replies.map((reply) => (
            <div key={reply.id} className="reply">
              <Avatar name={reply.author.name} kind={authorKind(reply.author, people)} />
              <div className="grow">
                <div className="thread-meta">
                  <span className="thread-author truncate">{reply.author.name}</span>
                  <span className="muted">{timeAgo(reply.createdAt)}</span>
                  {canComment && (mine(reply.author.id) || canManage) && editing !== reply.id && (
                    <span className="reply-actions">
                      <button type="button" className="link-btn" onClick={() => setEditing(reply.id)}>
                        Edit
                      </button>
                      <button type="button" className="link-btn" onClick={() => setConfirm({ kind: 'reply', reply })}>
                        Delete
                      </button>
                    </span>
                  )}
                </div>
                {editing === reply.id ? (
                  <CommentInput
                    projectId={projectId}
                    people={people}
                    placeholder="Edit reply"
                    submitLabel="Save"
                    initialBody={reply.body}
                    allowAttachments={false}
                    autoFocus
                    onCancel={() => setEditing(null)}
                    onSubmit={async (draft) => {
                      await actions.editReply(comment.id, reply.id, draft.body);
                      setEditing(null);
                    }}
                  />
                ) : (
                  <>
                    <Body text={reply.body} />
                    <Attachments items={reply.attachments} />
                  </>
                )}
              </div>
            </div>
          ))}

          {canComment && (
            <div className="thread-section">
              <CommentInput projectId={projectId} people={people} placeholder="Reply…" submitLabel="Reply" rows={1} onSubmit={(draft) => actions.reply(comment.id, draft)} />
            </div>
          )}

          {canComment && (
            <div className="thread-foot">
              {comment.status === 'resolved' ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => run(() => actions.setStatus(comment.id, 'open'))}>
                  <Icon name="refresh" /> Reopen
                </button>
              ) : (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => run(() => actions.setStatus(comment.id, 'resolved'))}>
                  <Icon name="check" /> Resolve
                </button>
              )}
              {comment.statusByName && comment.status !== 'open' && (
                <span className="muted truncate" style={{ fontSize: 'var(--fs-xs)' }}>
                  {STATUS_LABEL[comment.status]} by {comment.statusByName}
                </span>
              )}
            </div>
          )}
        </>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.kind === 'comment' ? `Delete revision #${comment.number}?` : 'Delete this reply?'}
          message={confirm.kind === 'comment' ? 'The revision, its replies and attachments will be removed for everyone. This cannot be undone.' : 'The reply will be removed for everyone.'}
          confirmLabel="Delete"
          danger
          onClose={() => setConfirm(null)}
          onConfirm={() => run(() => (confirm.kind === 'comment' ? actions.remove(comment.id) : actions.removeReply(comment.id, confirm.reply.id)))}
        />
      )}
    </article>
  );
}
